/**
 * Cutest Pluck Contest — frontend-side locked constants.
 *
 * Mirrors `backend/contest_entries.CONTEST_CONSENT_TEXT` and
 * `CONTEST_ID` byte-for-byte. Two independent constants, one
 * drift-guard test per side, catches any accidental rewrite on
 * either surface.
 *
 * DO NOT edit this file without a coordinated backend + legal
 * review dispatch. See the identical constants in
 * `backend/contest_entries.py`.
 */

export const CONTEST_ID = "cutest_pluck_2026";

export const CONTEST_CONSENT_TEXT =
  "I give PooPluckers permission to feature this photo and my " +
  "dog's name on the website and social media.";

export const MAX_DOG_NAME_LEN = 40;
export const MAX_CAPTION_LEN = 200;
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
export const ALLOWED_PHOTO_MIME = ["image/jpeg", "image/png"];

/* ------------------------------------------------------------------
 * Contest display-name accessor — 2026-07-24 (Item 3 frontend seam,
 * expanded 2026-07-24 by Item 1 build with defensive guard on the
 * `displayName` field to reach byte-parity with the backend γ-path
 * accessor at `backend/contest_entries.py::get_contest_display_name`).
 *
 * Public-facing surfaces (rules page, share OG copy) MUST NOT render
 * the raw internal round name (e.g. "cutest_pluck_2026 Round 1
 * (legacy)") that lives on `contest_rounds.name` when no admin has
 * set a friendly override. This accessor collapses three sources
 * into ONE choke point that all public renders flow through:
 *
 *   1. `activeContest.displayName` — Item 1 admin-editable public
 *      name. Wins over everything else WHEN clean (string, non-empty
 *      after trim, does not match INTERNAL_ROUND_ID_PATTERN). If
 *      set but internal-ID-shaped (an admin pastes the internal ID
 *      by mistake and bypasses the server validator), the accessor
 *      falls through — belt-and-braces defensive guard.
 *   2. `activeContest.contestName` — if it doesn't match the
 *      internal-ID pattern, it's assumed to be an admin-set friendly
 *      name and returned as-is.
 *   3. Hardcoded fallback — the brand string `"Cutest Pluck on the
 *      Block"`.
 *
 * `INTERNAL_ROUND_ID_PATTERN` is deliberately loose — a snake_case
 * `cutest_pluck_YYYY` prefix OR the string `Round N (legacy)`
 * anywhere in the name (case-insensitive) triggers the fallback.
 * Tight enough to catch the current legacy-synth name; loose enough
 * that a real future friendly name like "Cutest Pluck Round 2 —
 * Winter 2027" passes through unchanged.
 *
 * Admin surfaces (AdminContest*) MUST continue to display the raw
 * `contestName` field — they need to edit the internal identifier.
 * Do NOT introduce this accessor on admin surfaces.
 *
 * Cross-parity drift-guard: `contestConstants.parity.test.js` walks
 * both this accessor and the Python mirror through a SHA-pinned
 * byte-mirrored JSON fixture. Any semantic drift on either side
 * (regex, precedence, trim, guard) fails the test on that side.
 * ------------------------------------------------------------------
 */
export const CONTEST_DISPLAY_NAME_FALLBACK = "Cutest Pluck on the Block";

export const INTERNAL_ROUND_ID_PATTERN =
  /^cutest_pluck_\d{4}|Round\s+\d+\s*\(legacy\)/i;

export function getContestDisplayName(activeContest) {
  if (!activeContest || typeof activeContest !== "object") {
    return CONTEST_DISPLAY_NAME_FALLBACK;
  }
  // Item 1's admin-editable public display name — surfaces through the
  // context as `displayName`. When set + clean, wins over everything
  // else. Defensive belt-and-braces: if an admin's server-side validator
  // was bypassed (or a stale document pre-dates the Item 1 validator),
  // an internal-ID-shaped `displayName` MUST still short-circuit here
  // so no leak reaches a public render. Byte-parity with the Python
  // accessor at `backend/contest_entries.py::get_contest_display_name`.
  const displayName = activeContest.displayName;
  if (typeof displayName === "string") {
    const stripped = displayName.trim();
    if (stripped && !INTERNAL_ROUND_ID_PATTERN.test(stripped)) {
      return stripped;
    }
  }
  const contestName = activeContest.contestName;
  if (typeof contestName === "string") {
    const stripped = contestName.trim();
    if (stripped && !INTERNAL_ROUND_ID_PATTERN.test(stripped)) {
      return stripped;
    }
  }
  return CONTEST_DISPLAY_NAME_FALLBACK;
}
